import { useEffect, useState, useCallback } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';

/**
 * useCompanyLocationRecords
 * Fetches companyLocationRecords filtered by parentBreweryId.
 * Derives a numeric `date` timestamp (ms since epoch) from timestamps.createdDate where available.
 */
function useCompanyLocationRecords(parentBreweryId) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const toDateSafe = (value) => {
    if (!value) return 0;
    let recordDate = null;
    if (typeof value?.toDate === 'function') recordDate = value.toDate();
    else if (typeof value?.__time__ === 'string')
      recordDate = new Date(value.__time__);
    else if (typeof value === 'string' || value instanceof Date)
      recordDate = new Date(value);

    if (!recordDate) return 0;
    const ts = recordDate.getTime();
    return Number.isFinite(ts) ? ts : 0;
  };

  const refresh = useCallback(async () => {
    if (!parentBreweryId) {
      setRecords([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const collRef = collection(db, 'companyLocationRecords');
      const qry = query(
        collRef,
        where('parentBreweryId', '==', parentBreweryId),
      );
      const snap = await getDocs(qry);
      const rows = snap.docs.map((docSnap, index) => {
        const data = docSnap.data();
        const createdDate = toDateSafe(data?.timestamps?.createdDate);
        return {
          id: docSnap.id,
          ...data,
          date: createdDate, // numeric timestamp (ms) or 0
          __index: index, // stable fallback for tie-breaker
        };
      });
      // Sort newest first by derived date (numeric). Stable tie-breaker by id then original index.
      rows.sort((a, b) => {
        const at =
          typeof a.date === 'number' && Number.isFinite(a.date) ? a.date : 0;
        const bt =
          typeof b.date === 'number' && Number.isFinite(b.date) ? b.date : 0;
        if (bt !== at) return bt - at;
        const idCmp = String(a.id).localeCompare(String(b.id));
        if (idCmp !== 0) return idCmp;
        return a.__index - b.__index;
      });
      setRecords(rows);
    } catch {
      setError('Failed to load records');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [parentBreweryId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { records, loading, error, refresh };
}

export default useCompanyLocationRecords;
